import React from 'react';
import DOMPurify from 'dompurify';
import { Clock, User } from 'lucide-react';
import { getFileUrl } from '../lib/api';

const mediaUrl = (u) => { if (!u) return null; return u.startsWith('http') && !u.includes('/api/files/') ? u : getFileUrl(u); };
const looksLikeHtml = (s) => typeof s === 'string' && /<\/?[a-z][^>]*>/i.test(s);

export default function NewsPreview({ title, category, summary, content, imageUrl, videoUrl, author = 'Administrador' }) {
  const img = mediaUrl(imageUrl);
  const vid = mediaUrl(videoUrl);
  const today = new Date().toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  if (!title && !content) {
    return (
      <div data-testid="news-preview-empty" className="h-full flex flex-col items-center justify-center text-center p-10 text-[#0B1F40]/30">
        <p className="text-[10px] font-black tracking-[0.3em] uppercase mb-2">VISTA PREVIA</p>
        <p className="text-xs font-bold">Empieza a escribir para ver cómo se verá la noticia publicada.</p>
      </div>
    );
  }

  return (
    <article data-testid="news-preview" className="bg-white">
      {/* Hero image */}
      {img && (
        <div className="aspect-video bg-[#0B1F40] overflow-hidden border-b-2 border-[#0B1F40]">
          <img src={img} alt={title} className="w-full h-full object-cover" />
        </div>
      )}

      <div className="p-5 sm:p-6">
        {/* Category */}
        {category && (
          <div className="mb-3">
            <span className="inline-block bg-[#E50B15] text-white text-[9px] font-black tracking-[0.25em] uppercase px-2 py-1" style={{ fontFamily: 'Archivo Black' }}>
              {category}
            </span>
          </div>
        )}

        {/* Title */}
        <h1 className="text-2xl sm:text-3xl lg:text-4xl tracking-tighter text-[#0B1F40] leading-[1.05] mb-3" style={{ fontFamily: 'Archivo Black' }}>
          {title || 'Título de la noticia'}
        </h1>

        {/* Summary */}
        {summary && (
          <p className="text-sm sm:text-base text-[#0B1F40]/70 font-bold leading-relaxed mb-4 border-l-4 border-[#E50B15] pl-3">
            {summary}
          </p>
        )}

        {/* Meta */}
        <div className="flex flex-wrap items-center gap-3 text-[10px] font-black tracking-[0.15em] uppercase text-[#0B1F40]/40 border-y border-[#0B1F40]/10 py-2 mb-5">
          <span className="flex items-center gap-1"><User className="h-3 w-3" /> {author}</span>
          <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {today}</span>
        </div>

        {/* Content */}
        {content ? (
          looksLikeHtml(content) ? (
            <div className="prose prose-sm max-w-none text-[#0B1F40]/80 prose-headings:text-[#0B1F40] prose-headings:font-black prose-a:text-[#E50B15] prose-blockquote:border-l-4 prose-blockquote:border-[#E50B15] prose-img:border prose-img:border-[#0B1F40]/20"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content, { ADD_TAGS: ['iframe'], ADD_ATTR: ['allow', 'allowfullscreen', 'frameborder', 'scrolling', 'target', 'rel'] }) }} />
          ) : (
            <p className="text-sm leading-relaxed text-[#0B1F40]/70 whitespace-pre-wrap">{content}</p>
          )
        ) : (
          <p className="text-xs text-[#0B1F40]/30 italic">Escribe el contenido…</p>
        )}

        {/* Video */}
        {vid && (
          <div className="mt-6 border-2 border-[#0B1F40] overflow-hidden">
            <div className="bg-[#0B1F40] px-3 py-1.5">
              <span className="text-[9px] font-black tracking-[0.2em] uppercase text-white/60">VIDEO</span>
            </div>
            <video controls className="w-full max-h-[50vh] bg-black" src={vid} />
          </div>
        )}
      </div>
    </article>
  );
}
